import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPartnerVaultBalance, topUpPartnerVault } from '../../../../services/AccountService';
import { toast } from "react-toastify";
import {formatKenyanPhoneNumber} from "../../../../utils/helpers";

// variant controls the surface the same form is embedded on:
//   'vault'  (default): admin-team dashboard, shows the partner vault
//                       balance and speaks in vault language.
//   'wallet':           solo-user wallet dashboard, hides the vault
//                       balance display (always zero after auto-disburse)
//                       and speaks in wallet language. The endpoint the
//                       form hits is the same; the solo backend hook
//                       auto-disburses the vault credit into the sole
//                       active user's wallet on settlement.
const TopUpVaultForm = (props) => {
  const variant = props.variant === 'wallet' ? 'wallet' : 'vault';
  const isVaultVariant = variant === 'vault';
  const queryClient = useQueryClient();

  const { data: partnerVaultBalance, isFetching: partnerVaultBalanceLoading } = useQuery({
    queryKey: ['partnerVaultBalance', props.partnerId],
    queryFn: () => getPartnerVaultBalance(props.partnerId).then(res => res.data.balance),
    enabled: !!props.partnerId && isVaultVariant,
    staleTime: 0,
  });

  const [formData, setFormData] = useState({
    phoneNumber: '',
    amount: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    let finalValue;

    if (id === 'phoneNumber') {
      finalValue = formatKenyanPhoneNumber(value);
    } else {
      finalValue = value;
    }
    setFormData((prev) => ({ ...prev, [id]: finalValue }));
  };

  const topUpMutation = useMutation({
    mutationFn: (data) => topUpPartnerVault(props.partnerId, data),
  });

  const handleTopUpVault = (e) => {
    e.preventDefault();

    const submitAction = async () => {
      await topUpMutation.mutateAsync(formData);
      queryClient.invalidateQueries({ queryKey: ['partnerVaultBalance', props.partnerId] });
      queryClient.invalidateQueries({ queryKey: ['latestPartnerTopUps', props.partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partnerVaultTopUpCount', props.partnerId] });
      setFormData({ phoneNumber: '', amount: '' });
      props.onTopUpSubmitted?.();
    };

    toast.promise(
      submitAction(),
      {
        pending: 'Processing request ...',
        success: 'Your request will be processed shortly.',
        error: {
          render({ data: err }) {
            const fallback = isVaultVariant
              ? 'Failed to top up vault. Please try again later.'
              : 'Failed to top up wallet. Please try again later.';
            return err?.response?.data?.message || fallback;
          }
        }
      }
    );
  };

  const isAmountBelowMinimum = formData.amount !== '' && Number(formData.amount) < 5;
  const isAmountValid = formData.amount.trim() !== '' && Number(formData.amount) >= 5;
  const isFormValid = formData.phoneNumber && isAmountValid;

  return (
    <>
      <form
        onSubmit={handleTopUpVault}>
        <div className="sell-blance">
          {isVaultVariant && (
            <div className="form-label blance">
              <span style={{ marginRight: '5px' }}>BALANCE:</span>
              {partnerVaultBalanceLoading ? (
                <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
              ) : (
                <p>{partnerVaultBalance}</p>
              )}
            </div>
          )}
          <label className="form-label text-muted small" htmlFor="phoneNumber">Phone Number</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder=""
              id='phoneNumber'
              value={formData.phoneNumber}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="sell-blance">
          <label className="form-label text-muted small" htmlFor="amount">Amount</label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              placeholder=""
              id='amount'
              min="5"
              step="1"
              value={formData.amount}
              onChange={handleInputChange}
            />
          </div>
          {isAmountBelowMinimum && (
            <small className="text-danger mt-1 d-block">Minimum amount is KES 5.</small>
          )}
        </div>

        <div className="text-center">
          <button
            type="submit"
            className="btn btn-primary w-75"
            disabled={!isFormValid || topUpMutation.isPending}
          >
            {topUpMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing
              </>
            ) : (
              "Top Up"
            )}
          </button>
        </div>

      </form>
    </>
  )
}

export default TopUpVaultForm;
